import React, { useEffect, useState, useRef } from 'react';
import { Spin } from 'antd';
import styles from './LendAPIIframe.module.scss';
import FooterCard from '../../../prequal/components/FooterCard/FooterCard';
import PrequalProgressBar from '../../../prequal/components/PrequalProgressBar/PrequalProgressBar';
import {
  PREQUAL_PROGRESS_CONFIG,
  LendAPIProgressConfig,
} from '../../../prequal/prequalProgressConfig';
import { LendApiPrequalTab, LEND_API_ORIGIN, MAX_IFRAME_HEIGHT } from '../../../constants';

const IFRAME_LOAD_DEBOUNCE_MS = 500;
const FALLBACK_ACTIVATION_MS = 10000;
const MIN_IFRAME_HEIGHT = 100;

interface LendAPIIframeProps {
  src: string;
  iframeRef: React.RefObject<HTMLIFrameElement>;
  minHeight?: string;
  currentTab?: LendApiPrequalTab | string;
  lastKnownTab?: LendApiPrequalTab | string;
  progressConfig?: LendAPIProgressConfig<any>;
  showProgressBar?: boolean;
  isFullApp?: boolean;
  inModal?: boolean;
}

const getValueProp = <T extends string>(
  currentTab: T | undefined,
  config: LendAPIProgressConfig<T>
) => {
  if (!currentTab) {
    return {};
  }

  const valueProp = config.tabLookup.get(currentTab)?.valueProp ?? config.defaultValueProp;

  if (!valueProp || config.tabsWithNoValueProp?.has(currentTab)) {
    return {};
  }

  const items = valueProp.items ?? [];
  const { title, primaryIcon } = valueProp;
  const hasItems = items && items.length > 0;

  return { items, title, primaryIcon, hasItems };
};

const LendAPIIframe: React.FC<LendAPIIframeProps> = ({
  src,
  iframeRef,
  minHeight,
  currentTab,
  lastKnownTab,
  progressConfig = PREQUAL_PROGRESS_CONFIG,
  showProgressBar = false,
  isFullApp = false,
  inModal = false,
}) => {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [firstMessageReceived, setFirstMessageReceived] = useState(false);
  const loadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const customScriptActive = useRef(false);
  const fallbackActive = useRef(false);
  const { items, title, primaryIcon, hasItems } = getValueProp(currentTab, progressConfig);

  useEffect(() => {
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
    setIframeLoaded(false);
  }, [currentTab]);

  useEffect(() => {
    const fallbackTimeout = setTimeout(() => {
      if (!customScriptActive.current) {
        fallbackActive.current = true;
        console.error(
          '[LendAPI][IframeResizer][Parent] No iframeHeightChange received after 10s — custom height script may have been removed or replaced in LendAPI Product Studio. Falling back to dimensionChange.'
        );
      }
    }, FALLBACK_ACTIVATION_MS);

    const applyHeight = (height: number, source: string) => {
      if (
        iframeRef.current &&
        typeof height === 'number' &&
        height >= MIN_IFRAME_HEIGHT &&
        height <= MAX_IFRAME_HEIGHT &&
        Number.isFinite(height)
      ) {
        iframeRef.current.style.height = `${height}px`;
        console.log(
          `[LendAPI][IframeResizer][Parent] Height set to ${height}px (source: ${source})`
        );
        setFirstMessageReceived(true);

        setIframeLoaded(false);
        if (loadTimeoutRef.current) {
          clearTimeout(loadTimeoutRef.current);
        }
        loadTimeoutRef.current = setTimeout(() => {
          setIframeLoaded(true);
        }, IFRAME_LOAD_DEBOUNCE_MS);
      } else if (height > MAX_IFRAME_HEIGHT) {
        console.warn(
          `[LendAPI][IframeResizer][Parent] Height ${height}px exceeds maximum ${MAX_IFRAME_HEIGHT}px, ignoring`
        );
      }
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== LEND_API_ORIGIN) {
        return;
      }

      if (event.data && event.data.type === 'iframeHeightChange') {
        customScriptActive.current = true;
        const height = event.data.height ?? event.data.payload?.height;
        applyHeight(height, 'iframeHeightChange');
      } else if (event.data && event.data.type === 'dimensionChange' && fallbackActive.current) {
        const height = event.data.height ?? event.data.payload?.height;
        applyHeight(height, 'dimensionChange-fallback');
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(fallbackTimeout);
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
    };
    // iframeRef is stable and won't change during component lifecycle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`${styles.container}${inModal ? ` ${styles.containerInModal}` : ''}`}>
      {showProgressBar && currentTab && (
        <div
          className={`${styles.progressBarContainer} ${
            isFullApp ? styles.progressBarContainerFullApp : ''
          }`}
          data-testid="lendapi-iframe-progress-bar"
        >
          <PrequalProgressBar
            currentTab={currentTab}
            lastKnownTab={lastKnownTab}
            config={progressConfig as LendAPIProgressConfig<string>}
          />
        </div>
      )}
      {!firstMessageReceived && (
        <div className={styles.spinnerOverlay} data-testid="lendapi-loading-spinner">
          <Spin size="large" />
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={src}
        data-hj-allow-iframe="true"
        className={styles.lendApiFrame}
        style={minHeight ? { minHeight } : undefined}
        title="LendAPI Product Studio"
        scrolling="no"
      />
      {(isFullApp ? iframeLoaded : true) && !!hasItems && (
        <div className={styles.prequalFooterCardContainer}>
          <FooterCard items={items} title={title} icon={primaryIcon} />
        </div>
      )}
    </div>
  );
};

export default LendAPIIframe;
